import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';
import { IChingFigure, IChingLinePosition, IChingLineValue } from '../core/iching.models';

interface FigureRow {
  position: IChingLinePosition;
  yang: boolean;
  moving: boolean;
  y: number;
  highlighted: boolean;
  trigram: 'lower' | 'upper';
}

/**
 * Hexagramfiguur: zes lijnen onder→boven (lijn 1 onderaan), yin gebroken,
 * yang heel; bewegende lijnen met ○ (9) of × (6). Herbruikbaar in paneel,
 * pagina, dagboeklijst en mandala. Inputs: `lines` (worp) of `figure` (bits).
 * Een onvolledige worp (minder dan 6 lijnen) tekent alleen de aanwezige
 * lijnen, zodat de figuur van onder naar boven groeit.
 * `highlightLine` en `highlightLines` zetten lijnen (en hun bewegende
 * markering) in de accentkleur (`--hd-iching-highlight`, standaard `--primary`);
 * `dimOthers` vervaagt de overige lijnen zodat de gemarkeerde eruit springen.
 * `colorTrigrams` kleurt het onderste trigram (lijn 1-3, `--hd-iching-lower`,
 * standaard `--primary`) en het bovenste (lijn 4-6, `--hd-iching-upper`,
 * standaard `--amber`); bewegende markeringen volgen die kleur.
 * `baseTone="amber"` tekent alle niet-gemarkeerde lijnen in amber, zodat de
 * gemarkeerde (in `--primary`) zonder vervaging afsteken.
 * De maat staat op de host (`size`), de svg vult die breedte.
 */
@Component({
  selector: 'hd-iching-hexagram-figure',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': '"hd-iching-figure hd-iching-figure--" + size()' },
  styles: `
    :host { display: inline-block; flex: 0 0 auto; line-height: 0; width: 64px; }
    :host(.hd-iching-figure--mini) { width: 24px; }
    :host(.hd-iching-figure--large) { width: 120px; }
    svg { display: block; width: 100%; height: auto; }
    g { --_fill: var(--hd-iching-line, var(--text-primary, currentColor)); --_mark: var(--hd-iching-mark, var(--primary)); }
    .tone-amber { --_fill: var(--amber); }
    .is-lower { --_fill: var(--hd-iching-lower, var(--primary)); --_mark: var(--_fill); }
    .is-upper { --_fill: var(--hd-iching-upper, var(--amber)); --_mark: var(--_fill); }
    .is-highlighted { --_fill: var(--hd-iching-highlight, var(--primary)); --_mark: var(--_fill); }
    .line { fill: var(--_fill); }
    .is-dimmed { opacity: 0.35; }
    /* Open cirkel: gevuld met de achtergrondkleur zodat hij ook op een gekleurde lijn als 'gat' leesbaar blijft. */
    .mark-moving-yang { fill: var(--hd-iching-bg, var(--surface, #f5f2ea)); stroke: var(--_mark); stroke-width: 2; }
    .mark-moving-yin { stroke: var(--_mark); stroke-width: 2; }
  `,
  template: `
    <svg viewBox="0 0 100 92" [attr.aria-label]="ariaLabel() || undefined" role="img">
      @for (row of rows(); track row.position) {
        <g [attr.data-line]="row.position" [attr.data-y]="row.y"
           [class.is-yang]="row.yang" [class.is-yin]="!row.yang" [class.is-highlighted]="row.highlighted"
           [class.is-lower]="colorTrigrams() && row.trigram === 'lower'" [class.is-upper]="colorTrigrams() && row.trigram === 'upper'"
           [class.tone-amber]="baseTone() === 'amber'" [class.is-dimmed]="dimOthers() && hasHighlight() && !row.highlighted">
          @if (row.yang) {
            <rect class="line" x="10" [attr.y]="row.y" width="80" height="8" rx="1" />
          } @else {
            <rect class="line" x="10" [attr.y]="row.y" width="34" height="8" rx="1" />
            <rect class="line" x="56" [attr.y]="row.y" width="34" height="8" rx="1" />
          }
          @if (row.moving && row.yang) {
            <circle class="mark-moving-yang" cx="50" [attr.cy]="row.y + 4" r="5" />
          } @else if (row.moving) {
            <path class="mark-moving-yin" [attr.d]="'M46 ' + row.y + ' l8 8 M54 ' + row.y + ' l-8 8'" />
          }
        </g>
      }
    </svg>
  `,
})
export class HdIChingHexagramFigureComponent {
  readonly lines = input<readonly IChingLineValue[] | undefined>(undefined);
  readonly figure = input<IChingFigure | undefined>(undefined);
  readonly highlightLine = input<IChingLinePosition | undefined>(undefined);
  readonly highlightLines = input<readonly IChingLinePosition[]>([]);
  readonly dimOthers = input(false, { transform: booleanAttribute });
  /** Onderste trigram in `--primary`, bovenste in `--amber`. */
  readonly colorTrigrams = input(false, { transform: booleanAttribute });
  /** Kleur van de niet-gemarkeerde lijnen. */
  readonly baseTone = input<'default' | 'amber'>('default');
  readonly size = input<'mini' | 'normal' | 'large'>('normal');
  readonly ariaLabel = input<string>('');

  private readonly highlighted = computed<ReadonlySet<IChingLinePosition>>(() => {
    const single = this.highlightLine();
    return new Set(single === undefined ? this.highlightLines() : [...this.highlightLines(), single]);
  });

  protected readonly hasHighlight = computed(() => this.highlighted().size > 0);

  protected readonly rows = computed<FigureRow[]>(() => {
    const highlighted = this.highlighted();
    const lines = this.lines()?.slice(0, 6);
    const bits = lines ? lines.map(v => (v === 7 || v === 9 ? '1' : '0')).join('') : (this.figure() ?? '000000');
    const moving = lines ? lines.map(v => v === 6 || v === 9) : [false, false, false, false, false, false];
    return [...bits].map((bit, i) => ({
      position: (i + 1) as IChingLinePosition,
      yang: bit === '1',
      moving: moving[i] ?? false,
      y: 84 - i * 16,
      highlighted: highlighted.has((i + 1) as IChingLinePosition),
      trigram: i < 3 ? 'lower' : 'upper',
    }));
  });
}
